// Drawings tab inside the JobModal. Upload via SAS direct-to-Blob,
// poll for extraction completion, render matched-spool chips.
//
// Drag-drop is intentionally light — single or multi-PDF, no
// stitching/preview-before-upload; the workshop wants speed, not
// ceremony.
import { useEffect, useMemo, useRef, useState } from "react";
import type { DrawingDto, SpoolDto } from "../../../../../../shared/types/production-schedule";
import {
  addDrawingManualMatch,
  deleteDrawing,
  deleteDrawingMatch,
  dismissDrawingCandidate,
  ensureSpoolFromDrawing,
  getDrawingPreviewUrl,
  mintDrawingUploadUrl,
  promoteDrawingCandidate,
  registerDrawing,
  setDrawingMatchConfirmed,
} from "../hooks/useProductionApi";
import { spoolRefFromFilename } from "../lib/drawingHelpers";

interface Props {
  jobId: number;
  spools: SpoolDto[];
  // Drawings list lives one level up (JobModalBody) so the Spools tab
  // can render a per-spool "📐 Drawing" button without re-fetching.
  // Refresh callback is also lifted: any mutation here calls it to
  // keep both tabs in sync. Refresh errors come back via
  // drawingsError so the banner still surfaces when listDrawings
  // fails (network/auth/server down).
  drawings: DrawingDto[];
  drawingsError: string | null;
  onRefreshDrawings: () => Promise<void>;
  // Promoting a candidate inserts a new spool server-side; the parent
  // owns the spool list and needs to refetch so the new row appears
  // in JobModal + every other view. Other mutations on this tab
  // (dismiss, confirm, link, delete) only touch drawings/matches.
  onSpoolsChanged?: () => void | Promise<void>;
  // Append the freshly-promoted spool into JobModal's local draft
  // state so the next Save's replaceSpoolsBulk doesn't wipe it.
  // Without this, the user's Save click after a promote (or even a
  // Save races with the background refetch) DELETEs every spool the
  // drawings tab just created.
  onSpoolCreated?: (spool: SpoolDto) => void;
  // Cross-nav: switch to the Spools tab and focus the matched spool.
  onOpenSpool?: (spoolId: number) => void;
}

export function DrawingsTab({ jobId, spools, drawings, drawingsError, onRefreshDrawings, onSpoolsChanged, onSpoolCreated, onOpenSpool }: Props) {
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [query, setQuery] = useState<string>("");
  const [unlinkedOnly, setUnlinkedOnly] = useState<boolean>(false);
  const [selectedDrawings, setSelectedDrawings] = useState<Set<number>>(new Set());
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ done: number; total: number } | null>(null);

  const refresh = onRefreshDrawings;
  // Surface the refresh error first (covers initial load + poll
  // failures), then the most recent mutation error.
  const error = drawingsError ?? mutationError;
  const setError = setMutationError;

  // Poll while any drawing is still pending/running. The extractor
  // runs in the background on the server so the client refetches
  // until every drawing settles.
  useEffect(() => {
    const incomplete = drawings.some(
      (d) => d.extractionStatus === "pending" || d.extractionStatus === "running",
    );
    if (!incomplete) return;
    const handle = window.setInterval(() => { void refresh(); }, 2500);
    return () => window.clearInterval(handle);
  }, [drawings, refresh]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of Array.from(files)) {
        if (!f.type.includes("pdf") && !f.name.toLowerCase().endsWith(".pdf")) {
          throw new Error(`${f.name}: only PDF drawings are supported`);
        }
        const sas = await mintDrawingUploadUrl(jobId, f.name, f.type || "application/pdf");
        await uploadToSas(sas.uploadUrl, f, setUploadPct);
        await registerDrawing(jobId, {
          filename: f.name,
          blobPath: sas.blobPath,
          sizeBytes: f.size,
        });
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setUploadPct(null);
    }
  };

  const onRemove = async (drawingId: number) => {
    if (!window.confirm("Delete this drawing? Matches will be cleared too.")) return;
    try {
      await deleteDrawing(jobId, drawingId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onPreview = async (drawingId: number) => {
    try {
      const { url } = await getDrawingPreviewUrl(jobId, drawingId);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const toggleDrawingSelection = (id: number, checked: boolean) => {
    setSelectedDrawings((cur) => {
      const next = new Set(cur);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  // Sequential delete so the user sees a "n/m" counter and so a
  // mid-batch network failure leaves the surviving rows intact
  // (Promise.all would hide partial failures behind the first reject).
  // Intersect the selection with the live drawings list — a row may
  // have been deleted individually (per-row ×) or dropped by a
  // background refresh between selection and click, and firing DELETE
  // at a stale id surfaces an avoidable 404 in the error banner.
  const onBulkDelete = async () => {
    const liveIds = new Set(drawings.map((d) => d.id));
    const ids = Array.from(selectedDrawings).filter((id) => liveIds.has(id));
    if (ids.length === 0 || bulkDeleteProgress) {
      if (ids.length === 0 && selectedDrawings.size > 0) setSelectedDrawings(new Set());
      return;
    }
    if (!window.confirm(
      `Delete ${ids.length} drawing${ids.length === 1 ? "" : "s"}? Linked spools stay; only the PDFs and their match rows are removed.`,
    )) return;
    setBulkDeleteProgress({ done: 0, total: ids.length });
    setError(null);
    const failures: string[] = [];
    for (const id of ids) {
      try {
        await deleteDrawing(jobId, id);
      } catch (e) {
        failures.push(`#${id}: ${e instanceof Error ? e.message : String(e)}`);
      }
      setBulkDeleteProgress((cur) => cur ? { ...cur, done: cur.done + 1 } : cur);
    }
    setBulkDeleteProgress(null);
    setSelectedDrawings(new Set());
    await refresh();
    if (failures.length > 0) {
      setError(`Deleted ${ids.length - failures.length} of ${ids.length}. Failed:\n${failures.join("\n")}`);
    }
  };

  // Two sources of bulk-create work for the top button:
  //   1. Extraction candidates whose text isn't yet a spool num.
  //   2. Drawings with no matches at all but whose filename carries a
  //      recognisable ref — these come from incremental uploads after
  //      the first batch, where the workshop adds more PDFs and wants
  //      every new drawing to land its own spool.
  // Each entry is a (drawingId, num) pair; the run logic prefers
  // promoteDrawingCandidate when candidateId is present so the
  // candidate row gets cleaned up server-side.
  type BulkItem = {
    drawingId: number;
    candidateId?: number;
    num: string;
  };
  const bulkItems: BulkItem[] = (() => {
    const seenInJob = new Set(spools.map((s) => s.num.toUpperCase()));
    const seen = new Set<string>();
    const list: BulkItem[] = [];
    for (const d of drawings) {
      for (const c of d.candidates) {
        const key = c.text.toUpperCase();
        if (seenInJob.has(key) || seen.has(key)) continue;
        seen.add(key);
        list.push({ drawingId: d.id, candidateId: c.id, num: c.text });
      }
    }
    // Unlinked drawings with a derivable filename ref. Skip drawings
    // that already have any match (they're linked) and any whose
    // filename ref is already covered by the candidate pass above or
    // already a spool.
    for (const d of drawings) {
      if (d.extractionStatus !== "done") continue;
      if (d.matches.length > 0) continue;
      const ref = spoolRefFromFilename(d.filename);
      if (!ref) continue;
      const key = ref.toUpperCase();
      if (seenInJob.has(key) || seen.has(key)) continue;
      seen.add(key);
      list.push({ drawingId: d.id, num: ref });
    }
    return list;
  })();
  const [bulkBusy, setBulkBusy] = useState<{ done: number; total: number } | null>(null);

  const onCreateSpools = async () => {
    if (bulkItems.length === 0 || bulkBusy) return;
    if (!window.confirm(`Create ${bulkItems.length} spool${bulkItems.length === 1 ? "" : "s"} from the extracted refs?`)) return;
    setBulkBusy({ done: 0, total: bulkItems.length });
    setError(null);
    let succeeded = 0;
    const failures: string[] = [];
    // Sequential, not Promise.all: each insert into production_spools
    // computes sortOrder = max + 1. Running them in parallel races
    // the sortOrder calculation and lands several rows on the same
    // value, breaking the workshop's expected top-to-bottom order.
    for (const item of bulkItems) {
      try {
        if (item.candidateId !== undefined) {
          const { spool } = await promoteDrawingCandidate(jobId, item.drawingId, item.candidateId);
          onSpoolCreated?.(spool);
        } else {
          const { spool, created } = await ensureSpoolFromDrawing(jobId, item.drawingId);
          if (created) onSpoolCreated?.(spool);
        }
        succeeded += 1;
      } catch (e) {
        failures.push(`${item.num}: ${e instanceof Error ? e.message : String(e)}`);
      }
      setBulkBusy((cur) => cur ? { ...cur, done: cur.done + 1 } : cur);
    }
    setBulkBusy(null);
    await refresh();
    await onSpoolsChanged?.();
    if (failures.length > 0) {
      setError(`Created ${succeeded} of ${bulkItems.length}. Failed:\n${failures.join("\n")}`);
    }
  };

  const onCreateFromDrawing = async (drawingId: number, num?: string) => {
    try {
      const { spool, created } = await ensureSpoolFromDrawing(jobId, drawingId, num);
      if (created) onSpoolCreated?.(spool);
      await refresh();
      await onSpoolsChanged?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("Filename has no recognisable spool ref")) {
        const typed = window.prompt("Spool number for this drawing (e.g. SP-101, CT-CCW-7602):");
        if (!typed?.trim()) return;
        await onCreateFromDrawing(drawingId, typed.trim());
        return;
      }
      window.alert(`Could not create spool: ${msg}`);
    }
  };

  // Search filters drawings by filename, extracted candidate text,
  // OR by the num of any spool currently linked via a match row.
  // Workshop wants to type "SP-101" and see drawings linked to
  // SP-101 alongside drawings whose filename mentions it.
  const spoolById = useMemo(() => new Map(spools.map((s) => [s.id, s])), [spools]);
  // "Unlinked only" gates by match count: a drawing is linked once it
  // has at least one confirmed match row. Pending candidates don't
  // count — they're still in the "create or dismiss" queue and are
  // exactly what the user wants to surface via this filter.
  const unlinkedCount = useMemo(() => drawings.filter((d) => d.matches.length === 0).length, [drawings]);
  const filteredDrawings = useMemo(() => {
    const q = query.trim().toLowerCase();
    return drawings.filter((d) => {
      if (unlinkedOnly && d.matches.length > 0) return false;
      if (q === "") return true;
      if (d.filename.toLowerCase().includes(q)) return true;
      if (d.candidates.some((c) => c.text.toLowerCase().includes(q))) return true;
      if (d.matches.some((m) => spoolById.get(m.spoolId)?.num.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [drawings, query, unlinkedOnly, spoolById]);
  // Reconcile the displayed selection against live drawings so the
  // toolbar count + Delete N button match what bulk-delete will
  // actually issue (drawings can disappear via per-row delete or a
  // background refresh between selection and click).
  const effectiveSelectedCount = useMemo(() => {
    const live = new Set(drawings.map((d) => d.id));
    let n = 0;
    for (const id of selectedDrawings) if (live.has(id)) n++;
    return n;
  }, [drawings, selectedDrawings]);

  return (
    <div>
      <UploadDropZone onFiles={handleFiles} busy={busy} pct={uploadPct} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search filename, candidate, or linked spool #…"
          aria-label="Search drawings"
          style={{
            padding: "6px 10px",
            fontSize: 12,
            border: "1px solid var(--bsp-border-strong)",
            borderRadius: 4,
            flex: 1,
            fontFamily: "var(--bsp-font-sans)",
          }}
        />
        <label
          title="Show only drawings with no linked spool yet"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            padding: "6px 10px",
            border: "1px solid var(--bsp-border-strong)",
            borderRadius: 4,
            background: unlinkedOnly ? "var(--bsp-blue-pale, rgba(27,68,156,0.08))" : "var(--bsp-surface)",
            cursor: drawings.length === 0 ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
            fontWeight: unlinkedOnly ? 600 : 400,
          }}
        >
          <input
            type="checkbox"
            checked={unlinkedOnly}
            disabled={drawings.length === 0}
            onChange={(e) => setUnlinkedOnly(e.target.checked)}
          />
          Unlinked only{drawings.length > 0 ? ` (${unlinkedCount})` : ""}
        </label>
      </div>
      {drawings.length > 0 && (() => {
        const filteredIds = filteredDrawings.map((d) => d.id);
        const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedDrawings.has(id));
        const onToggleAll = (checked: boolean) => {
          setSelectedDrawings((cur) => {
            const next = new Set(cur);
            if (checked) for (const id of filteredIds) next.add(id);
            else for (const id of filteredIds) next.delete(id);
            return next;
          });
        };
        return (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 10px",
            marginTop: 10,
            background: "var(--bsp-surface-muted)",
            border: "1px solid var(--bsp-border)",
            borderRadius: 4,
            fontSize: 12,
          }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, cursor: filteredIds.length === 0 ? "not-allowed" : "pointer" }}>
              <input
                type="checkbox"
                checked={allFilteredSelected}
                disabled={filteredIds.length === 0}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
              Select all{query.trim() !== "" ? " (matching)" : ""}
            </label>
            <span style={{ fontFamily: "var(--bsp-font-mono)", color: "var(--bsp-fg-muted)" }}>
              {effectiveSelectedCount} selected
            </span>
            <div style={{ flex: 1 }} />
            {effectiveSelectedCount > 0 && (
              <button
                type="button"
                className="btn-sm"
                onClick={() => setSelectedDrawings(new Set())}
                disabled={bulkDeleteProgress !== null}
              >
                Clear
              </button>
            )}
            {effectiveSelectedCount > 0 && (
              <button
                type="button"
                className="btn-sm danger"
                onClick={() => void onBulkDelete()}
                disabled={bulkDeleteProgress !== null}
                title="Delete the selected drawings and their match rows"
              >
                {bulkDeleteProgress
                  ? `Deleting ${bulkDeleteProgress.done}/${bulkDeleteProgress.total}…`
                  : `Delete ${effectiveSelectedCount}`}
              </button>
            )}
          </div>
        );
      })()}
      {bulkItems.length > 0 && (
        <div style={{
          marginTop: 10,
          padding: "10px 12px",
          background: "var(--bsp-blue-pale, rgba(27,68,156,0.06))",
          border: "1px solid var(--bsp-blue-border, rgba(27,68,156,0.30))",
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}>
          <div style={{ fontSize: 12, color: "var(--bsp-dark)", flex: 1 }}>
            <strong>{bulkItems.length}</strong> new spool{bulkItems.length === 1 ? "" : "s"} ready from {drawings.length} drawing{drawings.length === 1 ? "" : "s"}. Click to add them to this job's spool list.
          </div>
          <button
            type="button"
            className="btn-submit"
            onClick={() => void onCreateSpools()}
            disabled={bulkBusy !== null}
          >
            {bulkBusy
              ? `Creating ${bulkBusy.done}/${bulkBusy.total}…`
              : `Create ${bulkItems.length} spool${bulkItems.length === 1 ? "" : "s"}`}
          </button>
        </div>
      )}
      {error && (
        <div style={{ background: "var(--bsp-red-tint)", color: "var(--bsp-red-fg)", padding: 10, borderRadius: 4, marginTop: 10, fontSize: 12, whiteSpace: "pre-line" }}>
          {error}
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        {drawings.length === 0 && (
          <p style={{ fontSize: 12, color: "var(--bsp-fg-muted)", textAlign: "center", padding: 18 }}>
            No drawings yet. Drop a PDF above to extract spool refs.
          </p>
        )}
        {drawings.length > 0 && filteredDrawings.length === 0 && (
          <p style={{ fontSize: 12, color: "var(--bsp-fg-muted)", textAlign: "center", padding: 18 }}>
            {unlinkedOnly && query.trim() === ""
              ? "Every drawing is linked to a spool."
              : unlinkedOnly
                ? `No unlinked drawings match "${query}".`
                : `No drawings match "${query}".`}
          </p>
        )}
        {filteredDrawings.map((d) => (
          <DrawingRow
            key={d.id}
            drawing={d}
            spools={spools}
            jobId={jobId}
            selected={selectedDrawings.has(d.id)}
            onSelectToggle={(checked) => toggleDrawingSelection(d.id, checked)}
            onRefresh={refresh}
            onSpoolsChanged={onSpoolsChanged}
            onSpoolCreated={onSpoolCreated}
            onPreview={() => void onPreview(d.id)}
            onRemove={() => void onRemove(d.id)}
            onCreateSpool={() => void onCreateFromDrawing(d.id)}
            onOpenSpool={onOpenSpool}
          />
        ))}
      </div>
    </div>
  );
}

function UploadDropZone({
  onFiles,
  busy,
  pct,
}: {
  onFiles: (files: FileList | null) => void;
  busy: boolean;
  pct: number | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        onFiles(e.dataTransfer.files);
      }}
      style={{
        border: `2px dashed ${dragging ? "var(--bsp-blue)" : "var(--bsp-border-strong)"}`,
        background: dragging ? "var(--bsp-blue-pale)" : "var(--bsp-surface-muted)",
        borderRadius: 6,
        padding: "20px 14px",
        textAlign: "center",
        cursor: busy ? "wait" : "pointer",
        transition: "all 120ms",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        style={{ display: "none" }}
        onChange={(e) => onFiles(e.target.files)}
      />
      {busy ? (
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          Uploading{pct !== null ? ` ${pct}%` : ""}…
        </span>
      ) : (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--bsp-fg)" }}>
            Drop PDFs here or click to choose
          </div>
          <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginTop: 4, lineHeight: 1.5 }}>
            Each page is scanned for spool refs (e.g. SP-001, CT-CCW-7602) and auto-linked to spools in this job.
          </div>
        </>
      )}
    </div>
  );
}

function DrawingRow({
  drawing,
  spools,
  jobId,
  selected,
  onSelectToggle,
  onRefresh,
  onSpoolsChanged,
  onSpoolCreated,
  onPreview,
  onRemove,
  onCreateSpool,
  onOpenSpool,
}: {
  drawing: DrawingDto;
  spools: SpoolDto[];
  jobId: number;
  selected: boolean;
  onSelectToggle: (checked: boolean) => void;
  onRefresh: () => Promise<void>;
  onSpoolsChanged?: () => void | Promise<void>;
  onSpoolCreated?: (spool: SpoolDto) => void;
  onPreview: () => void;
  onRemove: () => void;
  onCreateSpool: () => void;
  onOpenSpool?: (spoolId: number) => void;
}) {
  const [linking, setLinking] = useState(false);
  const spoolById = new Map(spools.map((s) => [s.id, s]));
  const statusBadge = STATUS_BADGE[drawing.extractionStatus];
  const sizeKb = drawing.sizeBytes ? Math.round(drawing.sizeBytes / 1024) : null;
  const hasNoMatches = drawing.matches.length === 0;
  const filenameRef = spoolRefFromFilename(drawing.filename);

  return (
    <div style={{
      border: selected ? "1px solid var(--bsp-blue)" : "1px solid var(--bsp-border)",
      borderRadius: 4,
      padding: 10,
      marginBottom: 8,
      background: selected ? "var(--bsp-blue-pale, rgba(27,68,156,0.06))" : "var(--bsp-surface)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelectToggle(e.target.checked)}
          aria-label={`Select ${drawing.filename}`}
          title="Select for bulk delete"
        />
        <span style={{ fontSize: 14 }}>📄</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--bsp-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {drawing.filename}
          </div>
          <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)", marginTop: 2 }}>
            {new Date(drawing.uploadedAt).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}
            {sizeKb !== null && ` · ${sizeKb} KB`}
            {drawing.pageCount && ` · ${drawing.pageCount} page${drawing.pageCount === 1 ? "" : "s"}`}
          </div>
        </div>
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
          padding: "3px 8px", borderRadius: 999,
          background: statusBadge.bg, color: statusBadge.fg,
        }}>
          {statusBadge.label}
        </span>
        <button className="btn-sm" onClick={onPreview} title="Open the PDF in a new tab">View</button>
        <button className="btn-sm danger" onClick={onRemove} aria-label="Delete">×</button>
      </div>
      {drawing.extractionStatus === "failed" && drawing.extractionError && (
        <div style={{ fontSize: 11, color: "var(--bsp-red-fg)", marginTop: 6 }}>
          {drawing.extractionError}
        </div>
      )}
      {drawing.matches.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
          {drawing.matches.map((m) => {
            const sp = spoolById.get(m.spoolId);
            return (
              <SpoolMatchChip
                key={m.id}
                spoolNum={sp?.num ?? `#${m.spoolId}`}
                pageNumber={m.pageNumber}
                matchType={m.matchType}
                confirmed={m.confirmed}
                onOpen={onOpenSpool ? () => onOpenSpool(m.spoolId) : undefined}
                onConfirmToggle={async () => {
                  await setDrawingMatchConfirmed(jobId, drawing.id, m.id, !m.confirmed);
                  await onRefresh();
                }}
                onRemove={async () => {
                  await deleteDrawingMatch(jobId, drawing.id, m.id);
                  await onRefresh();
                }}
              />
            );
          })}
        </div>
      )}
      {drawing.extractionStatus === "done" && hasNoMatches && (
        <button
          type="button"
          className="btn-sm"
          style={{
            marginTop: 8,
            background: "var(--bsp-blue)",
            color: "#fff",
            borderColor: "var(--bsp-blue)",
            fontWeight: 700,
          }}
          onClick={onCreateSpool}
          title={
            filenameRef
              ? `Create spool ${filenameRef} and link this drawing`
              : "Enter a spool number and link this drawing"
          }
        >
          + Create spool{filenameRef ? ` ${filenameRef}` : ""}
        </button>
      )}
      {drawing.candidates.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--bsp-fg-muted)", marginBottom: 4 }}>
            New spool candidates
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {drawing.candidates.map((c) => (
              <CandidateChip
                key={c.id}
                text={c.text}
                pageNumber={c.pageNumber}
                onPromote={async () => {
                  try {
                    const { spool } = await promoteDrawingCandidate(jobId, drawing.id, c.id);
                    // Push the freshly-created spool into the modal's
                    // local draft state FIRST, before the parent's
                    // refetch fires. The next replaceSpoolsBulk (Save)
                    // now includes this row instead of deleting it.
                    onSpoolCreated?.(spool);
                    await onRefresh();
                    await onSpoolsChanged?.();
                  } catch (e) {
                    window.alert(`Could not create spool from "${c.text}": ${e instanceof Error ? e.message : String(e)}`);
                    throw e;
                  }
                }}
                onDismiss={async () => {
                  try {
                    await dismissDrawingCandidate(jobId, drawing.id, c.id);
                    await onRefresh();
                  } catch (e) {
                    window.alert(`Could not dismiss "${c.text}": ${e instanceof Error ? e.message : String(e)}`);
                    throw e;
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}
      {linking ? (
        <ManualLink
          spools={spools}
          existingSpoolIds={new Set(drawing.matches.map((m) => m.spoolId))}
          onCancel={() => setLinking(false)}
          onLink={async (spoolId) => {
            await addDrawingManualMatch(jobId, drawing.id, spoolId, null);
            setLinking(false);
            await onRefresh();
          }}
        />
      ) : (
        drawing.extractionStatus === "done" && (
          <button
            className="btn-sm"
            style={{ marginTop: 6 }}
            onClick={() => setLinking(true)}
          >
            + Link a spool manually
          </button>
        )
      )}
    </div>
  );
}

function SpoolMatchChip({
  spoolNum,
  pageNumber,
  matchType,
  confirmed,
  onOpen,
  onConfirmToggle,
  onRemove,
}: {
  spoolNum: string;
  pageNumber: number | null;
  matchType: "exact" | "pattern" | "manual";
  confirmed: boolean;
  onOpen?: () => void;
  onConfirmToggle: () => void;
  onRemove: () => void;
}) {
  const bg = confirmed ? "var(--bsp-green-tint, #DCFCE7)" : "var(--bsp-blue-pale)";
  const fg = confirmed ? "var(--bsp-green-fg, #166534)" : "var(--bsp-blue)";
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 8px",
      background: bg,
      color: fg,
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      fontFamily: "var(--bsp-font-mono)",
    }}>
      {confirmed ? "✓ " : ""}
      {onOpen ? (
        <button
          onClick={onOpen}
          title={`Open spool ${spoolNum}`}
          style={{ background: "none", border: "none", color: fg, fontFamily: "inherit", fontSize: "inherit", fontWeight: "inherit", cursor: "pointer", padding: 0, textDecoration: "underline" }}
        >
          {spoolNum}
        </button>
      ) : (
        spoolNum
      )}
      {pageNumber !== null && <span style={{ opacity: 0.7 }}>·p{pageNumber}</span>}
      <span style={{ opacity: 0.6, fontSize: 9 }}>{matchType}</span>
      <button
        onClick={onConfirmToggle}
        title={confirmed ? "Mark as unconfirmed" : "Confirm match"}
        style={{ background: "none", border: "none", color: fg, fontSize: 12, cursor: "pointer", padding: 0 }}
      >
        {confirmed ? "↺" : "✓"}
      </button>
      <button
        onClick={onRemove}
        title="Unlink"
        style={{ background: "none", border: "none", color: fg, fontSize: 12, cursor: "pointer", padding: 0 }}
      >
        ×
      </button>
    </span>
  );
}

function CandidateChip({
  text,
  pageNumber,
  onPromote,
  onDismiss,
}: {
  text: string;
  pageNumber: number | null;
  onPromote: () => Promise<void>;
  onDismiss: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<"promote" | "dismiss" | null>(null);
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 8px",
      background: "#FEF3C7",
      color: "#92400E",
      border: "1px dashed #F59E0B",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      fontFamily: "var(--bsp-font-mono)",
    }}>
      {text}
      {pageNumber !== null && <span style={{ opacity: 0.7 }}>·p{pageNumber}</span>}
      <button
        onClick={async () => {
          setBusy("promote");
          try { await onPromote(); } finally { setBusy(null); }
        }}
        disabled={busy !== null}
        title="Create a new spool with this number and link the drawing"
        style={{ background: "var(--bsp-blue)", color: "#fff", border: "none", padding: "1px 6px", borderRadius: 999, fontSize: 9, fontWeight: 700, cursor: busy === null ? "pointer" : "wait" }}
      >
        {busy === "promote" ? "…" : "+ Create"}
      </button>
      <button
        onClick={async () => {
          setBusy("dismiss");
          try { await onDismiss(); } finally { setBusy(null); }
        }}
        disabled={busy !== null}
        title="Dismiss this candidate"
        style={{ background: "none", border: "none", color: "#92400E", fontSize: 12, cursor: "pointer", padding: 0 }}
      >
        ×
      </button>
    </span>
  );
}

function ManualLink({
  spools,
  existingSpoolIds,
  onCancel,
  onLink,
}: {
  spools: SpoolDto[];
  existingSpoolIds: Set<number>;
  onCancel: () => void;
  onLink: (spoolId: number) => Promise<void>;
}) {
  const available = spools.filter((s) => !existingSpoolIds.has(s.id));
  if (available.length === 0) {
    return (
      <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginTop: 6 }}>
        Every spool in this job is already linked.
        <button className="btn-sm" style={{ marginLeft: 8 }} onClick={onCancel}>Cancel</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center" }}>
      <select
        defaultValue=""
        onChange={(e) => { if (e.target.value) void onLink(Number(e.target.value)); }}
        style={{ flex: 1, padding: 6, fontSize: 12, border: "1px solid var(--bsp-border-strong)", borderRadius: 4 }}
      >
        <option value="">— Choose a spool —</option>
        {available.map((s) => (
          <option key={s.id} value={s.id}>{s.num}{s.description ? ` · ${s.description}` : ""}</option>
        ))}
      </select>
      <button className="btn-sm" onClick={onCancel}>Cancel</button>
    </div>
  );
}

const STATUS_BADGE: Record<DrawingDto["extractionStatus"], { label: string; bg: string; fg: string }> = {
  pending: { label: "Queued", bg: "var(--bsp-surface-muted)", fg: "var(--bsp-fg-muted)" },
  running: { label: "Scanning…", bg: "#FEF3C7", fg: "#92400E" },
  done: { label: "Scanned", bg: "var(--bsp-green-tint, #DCFCE7)", fg: "var(--bsp-green-fg, #166534)" },
  failed: { label: "Failed", bg: "var(--bsp-red-tint)", fg: "var(--bsp-red-fg)" },
};

// Tiny XHR-based uploader. fetch() doesn't expose upload progress, so
// XHR is the simplest way to show a percentage while large drawings
// stream to Azure Blob.
async function uploadToSas(
  uploadUrl: string,
  file: File,
  onPct: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
    xhr.setRequestHeader("Content-Type", file.type || "application/pdf");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onPct(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed (network)"));
    xhr.send(file);
  });
}
